import { Icon } from '@iconify/react'
import {
  RASPADINHA_DEFAULT_PRIMARY_COLOR,
  RASPADINHA_DEFAULT_SECONDARY_COLOR,
} from '@/lib/raspadinha-campaigns'
import { ensureContemplatedPrizeImages } from '@/lib/raspadinha-campaigns'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'
import { RaspadinhaColorPickerField } from './RaspadinhaColorPickerField'
import { RaspadinhaContemplatedPrizesEditor } from './RaspadinhaContemplatedPrizesEditor'
import { RaspadinhaImageUploadField } from './RaspadinhaImageUploadField'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './RaspadinhaMidiaSection.module.css'

type RaspadinhaMidiaSectionProps = {
  form: RaspadinhaCampaign
  onFormChange: (next: RaspadinhaCampaign) => void
  disabled?: boolean
  onWelcomeImageChange: (url: string, file?: File | null) => void
  onBackgroundImageChange: (url: string, file?: File | null) => void
  onContemplatedImageChange: (url: string, file?: File | null) => void
  onNotContemplatedImageChange: (url: string, file?: File | null) => void
  onPrizeImageChange: (index: number, url: string, file?: File | null) => void
}

export function RaspadinhaMidiaSection({
  form,
  onFormChange,
  disabled = false,
  onWelcomeImageChange,
  onBackgroundImageChange,
  onContemplatedImageChange,
  onNotContemplatedImageChange,
  onPrizeImageChange,
}: RaspadinhaMidiaSectionProps) {
  const updateField = <K extends keyof RaspadinhaCampaign>(field: K, value: RaspadinhaCampaign[K]) => {
    onFormChange({ ...form, [field]: value })
  }

  const contemplatedPrizes = ensureContemplatedPrizeImages(form.contemplated_prize_images)

  const secondaryColorLabel = form.secondary_color.trim() || RASPADINHA_DEFAULT_SECONDARY_COLOR

  return (
    <section className={editStyles.editSection} aria-labelledby="raspadinha-midia">
      <h2 id="raspadinha-midia" className={editStyles.editSectionTitle}>
        <Icon className={editStyles.editSectionTitleIcon} icon="mdi:palette-outline" />
        Mídia e prêmios
      </h2>
      <div className={editStyles.editSectionBody}>
        <p className={styles.mediaExamplesHint}>
          As imagens exibidas por padrão são apenas exemplos. Você pode substituí-las ou removê-las a
          qualquer momento.
        </p>
        <div className={editStyles.editSubsection}>
          <h3 className={editStyles.editSubsectionTitle}>Paleta de cores</h3>
          <p className={styles.paletteHint}>
            Clique no quadrado de cor para abrir o seletor. O código hexadecimal pode ser copiado ao lado.
          </p>
          <div className={styles.colorPickersGrid}>
            <RaspadinhaColorPickerField
              id="raspadinha-primary"
              label="Cor primária"
              hint="Títulos, botões e destaques"
              value={form.primary_color}
              fallbackColor={RASPADINHA_DEFAULT_PRIMARY_COLOR}
              onChange={(color) => updateField('primary_color', color)}
            />
            <RaspadinhaColorPickerField
              id="raspadinha-secondary"
              label="Cor secundária"
              hint="Badges, acentos e detalhes"
              value={form.secondary_color}
              fallbackColor={RASPADINHA_DEFAULT_SECONDARY_COLOR}
              onChange={(color) => updateField('secondary_color', color)}
            />
          </div>
        </div>

        <div className={editStyles.editSubsection}>
          <h3 className={editStyles.editSubsectionTitle}>Boas Vindas</h3>
          <p className={styles.welcomeHint}>
            Esta será a primeira imagem que o participante visualizará na página pública. Abaixo dela
            serão exibidos os termos e regulamentos da campanha, com um botão para continuar para a
            próxima etapa.
          </p>
          <div className={styles.welcomeImageField}>
            <RaspadinhaImageUploadField
              label="Boas Vindas"
              hint="(Arte de abertura exibida antes dos termos e do botão de continuar)"
              observations="A imagem ideal deve ser quadrada, em proporção 1:1, com 1000 px por 1000 px. Você tem liberdade para experimentar outras dimensões."
              imageUrl={form.welcome_image_url}
              onImageChange={onWelcomeImageChange}
              disabled={disabled}
            />
          </div>
        </div>

        <div className={editStyles.editSubsection}>
          <h3 className={editStyles.editSubsectionTitle}>Imagens da raspadinha</h3>
          <p className={styles.scratchImagesHint}>
            As três artes abaixo são exibidas na experiência de raspadinha. Todas devem seguir o padrão
            de proporção 9:16 (vertical) e usar no fundo a mesma cor secundária da campanha (
            {secondaryColorLabel}).
          </p>
          <div className={styles.scratchImagesLayout}>
            <div className={styles.scratchImagesGrid}>
              <RaspadinhaImageUploadField
                label="Imagem a ser raspada"
                hint="(Camada que o participante raspa na página pública)"
                imageUrl={form.background_image_url}
                onImageChange={onBackgroundImageChange}
                disabled={disabled}
              />
              <RaspadinhaImageUploadField
                label="Contemplado"
                hint="(Arte exibida quando o participante contempla prêmio)"
                imageUrl={form.contemplated_image_url}
                onImageChange={onContemplatedImageChange}
                disabled={disabled}
              />
              <RaspadinhaImageUploadField
                label="Não contemplado"
                hint="(Arte exibida quando o participante não contempla prêmio)"
                imageUrl={form.not_contemplated_image_url}
                onImageChange={onNotContemplatedImageChange}
                disabled={disabled}
              />
            </div>
            <RaspadinhaContemplatedPrizesEditor
              prizes={contemplatedPrizes}
              schedules={form.winning_date_schedules}
              startsAt={form.starts_at}
              endsAt={form.ends_at}
              disabled={disabled}
              onPrizesChange={(contemplated_prize_images) =>
                onFormChange({ ...form, contemplated_prize_images })
              }
              onPrizeImageChange={onPrizeImageChange}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
